/* =============================================================================
   Quick Order  -  assets/quick-order.js

   Fixed 2026-08-21. Three bugs, all confirmed live on /collections/quick-order:

   1. The CSV / SKU-list upload did nothing at all.
      quickOrderPopulate() called quickOrderApp.addToQuickOrder(), but
      quickOrderApp is a `var` declared INSIDE the DOMContentLoaded callback in
      the quick-order template, so it is not global. Every upload threw
      "quickOrderApp is not defined" and died silently - it never even reached
      the code that shows the invalid-SKU message, so the page just sat there.
      This file no longer touches the Vue app. It appends rows itself.

   2. Only first-variant SKUs could be ordered.
      quotes.arborsci.com only resolves each product's FIRST variant SKU. Of the
      42 variant SKUs in the catalog, all 13 first-variant SKUs resolve and all
      29 others return nothing, so a customer asking for P1-1065-12 (the 12/pk
      meter stick) was told their SKU was invalid. Anything the middleware
      misses now falls back to Boost's search index, which does carry every
      variant SKU, and the matching variant is preselected on the row.

   3. Changing the variant dropdown updated the price but not the SKU.
      The old handler read the SKU back out of the ?view=quickorder HTML, and
      that template always renders variants.first.sku no matter which variant
      is requested. So the row would read "P1-1065-06 ... $70.50" - the 6/pk
      SKU at the 12/pk price. The cart still got the right variant (the select
      carries name="id"), but the customer was reading a wrong SKU off the
      screen and onto their PO. The SKU now comes from the variant itself.

   Matching rule: a SKU only ever resolves on an EXACT match, ignoring case and
   punctuation only (so 44-1090 and 441090 are the same SKU). Boost's fuzzy
   search is used to FIND candidates, never to pick one. If nothing matches
   exactly the SKU is reported invalid rather than quietly swapped for a near
   miss, because "we don't stock that" is information the customer needs.
   ============================================================================= */

/* -----------------------------------------------------------------------------
   Small helpers
   -------------------------------------------------------------------------- */

// Punctuation- and case-insensitive SKU key. Deliberately NOT typo-tolerant.
function qoNormalizeSku(sku) {
    return String(sku == null ? '' : sku).toLowerCase().replace(/[\s\-_.]/g, '');
}

function qoMiddlewareUrl() {
    return (typeof ArborSciConfig !== 'undefined' && ArborSciConfig.middlewareUrl)
        ? ArborSciConfig.middlewareUrl
        : 'https://quotes.arborsci.com';
}

function qoShopDomain() {
    return (typeof Shopify !== 'undefined' && Shopify.shop)
        ? Shopify.shop
        : 'arbor-scientific.myshopify.com';
}

// Run `worker` over `items` with at most `limit` in flight, so a 100-row CSV
// doesn't fire 100 simultaneous requests. Resolves with results in input order.
function qoMapLimit(items, limit, worker) {
    var results = new Array(items.length);
    var next = 0;

    function run() {
        if (next >= items.length) return Promise.resolve();
        var i = next++;
        return Promise.resolve(worker(items[i], i)).then(function (value) {
            results[i] = value;
            return run();
        });
    }

    var runners = [];
    for (var k = 0; k < Math.min(limit, items.length); k++) runners.push(run());
    return Promise.all(runners).then(function () { return results; });
}

/* -----------------------------------------------------------------------------
   Product data (cached - the same handle gets hit by both the SKU lookup and
   the variant dropdown, and /products/x.js is the only place with real SKUs)
   -------------------------------------------------------------------------- */

var qoProductCache = {};

function qoGetProduct(handle) {
    if (!qoProductCache[handle]) {
        qoProductCache[handle] = new Promise(function (resolve) {
            $.ajax({ url: '/products/' + handle + '.js', dataType: 'json' })
                .done(function (product) { resolve(product); })
                .fail(function () { resolve(null); });
        });
    }
    return qoProductCache[handle];
}

function qoFindVariantBySku(product, sku) {
    if (!product || !product.variants) return null;
    var target = qoNormalizeSku(sku);
    if (!target) return null;
    for (var i = 0; i < product.variants.length; i++) {
        if (qoNormalizeSku(product.variants[i].sku) === target) return product.variants[i];
    }
    return null;
}

function qoFindVariantById(product, variantId) {
    if (!product || !product.variants) return null;
    for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === String(variantId)) return product.variants[i];
    }
    return null;
}

/* -----------------------------------------------------------------------------
   SKU resolution
   -------------------------------------------------------------------------- */

// The existing middleware. Fast, one call for the whole list, but it only knows
// each product's first variant SKU.
function qoResolveViaMiddleware(skuList) {
    return new Promise(function (resolve) {
        if (!skuList.length) { resolve({}); return; }

        $.get(qoMiddlewareUrl() + '/api/quickorder/products', { skus: skuList }, function (response) {
            var map = {};
            var products = (response && response.products) || [];
            for (var i = 0; i < products.length; i++) {
                if (products[i] && products[i].sku && products[i].handle) {
                    map[qoNormalizeSku(products[i].sku)] = products[i].handle;
                }
            }
            resolve(map);
        }, 'json').fail(function () {
            resolve({});
        });
    });
}

// Fallback for anything the middleware doesn't know. Boost indexes every
// variant SKU on a product and returns them in a `skus` array, so we can
// confirm an exact hit instead of trusting its fuzzy ranking.
function qoBoostLookupSku(sku) {
    return new Promise(function (resolve) {
        $.ajax({
            url: 'https://services.mybcapps.com/bc-sf-filter/search/suggest',
            dataType: 'json',
            data: {
                shop: qoShopDomain(),
                q: sku,
                product_limit: 10,
                suggestion_limit: 0,
                collection_limit: 0,
                page_limit: 0,
                dym_limit: 0
            }
        }).done(function (data) {
            var products = (data && data.products) || [];
            var target = qoNormalizeSku(sku);

            for (var i = 0; i < products.length; i++) {
                var skus = products[i].skus || [];
                for (var j = 0; j < skus.length; j++) {
                    if (qoNormalizeSku(skus[j]) === target) {
                        resolve(products[i].handle);
                        return;
                    }
                }
            }
            // Boost may well have returned something similar. We don't want it.
            resolve(null);
        }).fail(function () {
            resolve(null);
        });
    });
}

function qoResolveViaBoost(skuList) {
    if (!skuList.length) return Promise.resolve({});

    return qoMapLimit(skuList, 6, function (sku) {
        return qoBoostLookupSku(sku).then(function (handle) {
            return { sku: sku, handle: handle };
        });
    }).then(function (rows) {
        var map = {};
        for (var i = 0; i < rows.length; i++) {
            if (rows[i] && rows[i].handle) map[qoNormalizeSku(rows[i].sku)] = rows[i].handle;
        }
        return map;
    });
}

/* -----------------------------------------------------------------------------
   Row building
   -------------------------------------------------------------------------- */

// Fetch a row's HTML without appending it, so a multi-SKU upload can be
// requested in parallel but appended in the order the customer listed them.
function qoFetchRow(item) {
    return qoGetProduct(item.handle).then(function (product) {
        var variant = qoFindVariantBySku(product, item.sku);
        var url = '/products/' + item.handle + '?view=quickorder';
        if (variant) url += '&variant=' + variant.id;

        return new Promise(function (resolve) {
            $.get(url, function (html) {
                var unusable = !html
                    || html.indexOf('data-unavailable-product') > -1
                    || html.indexOf('<meta charset="utf-8">') > -1;
                resolve(unusable ? null : { html: html, variant: variant, item: item });
            }).fail(function () {
                resolve(null);
            });
        });
    });
}

function qoAppendRow(row) {
    var $items = $('.quickorder-container .qo-product-items');
    $items.append(row.html);

    var $row = $items.find('.product-item-wrapper').last();
    $row.find('.product-item-quantity').val(row.item.qty || 1);

    if (row.variant) qoApplyVariantToRow($row, row.variant);

    $('.quickorder-container .qo-populated').show();
    $('.quickorder-container .qo-remove').show();
    return $row;
}

// Point a row at a specific variant: select it in the dropdown and, critically,
// show that variant's own SKU rather than the first variant's.
function qoApplyVariantToRow($row, variant) {
    if (!variant) return;

    var $select = $row.find('select.qo-variant');
    if ($select.length && variant.id) $select.val(String(variant.id));

    if (variant.sku) {
        $row.attr('data-sku', variant.sku);
        $row.data('sku', variant.sku);
        $row.find('.qo-sku input').val(variant.sku);
    }
}

// Swap the thumbnail. Kept from the original, including the cache-bust and the
// data-src/data-srcset strip, which are there to stop lazyload putting the old
// image back after we've set the new one.
function qoUpdateRowImage($row, product, variant) {
    var $img = $row.find('.qo-img img').first();
    if (!$img.length || !product) return;

    var src = (variant && variant.featured_image)
        || product.featured_image
        || (product.images && product.images.length ? product.images[0] : null);
    if (!src) return;

    if (typeof src === 'object') src = src.src || src;
    src = String(src);

    if (src.indexOf('//') === 0) src = 'https:' + src;
    if (src.indexOf('cdn.shopify.com') > -1) src = src.replace(/\/(\d+)x(\d+)\//, '/100x100/');

    var imgElement = $img[0];
    if (!imgElement) return;

    var separator = src.indexOf('?') > -1 ? '&' : '?';
    var newSrc = src + separator + '_=' + Date.now();

    imgElement.src = '';
    $img.removeAttr('data-src');
    $img.removeAttr('data-srcset');
    setTimeout(function () { imgElement.src = newSrc; }, 10);
}

/* -----------------------------------------------------------------------------
   Invalid SKU reporting
   -------------------------------------------------------------------------- */

function qoClearInvalidMessage() {
    var existing = document.querySelector('.invalid-sku-message');
    if (existing) existing.remove();
}

// Left on window because the quick-order template's Vue app calls it.
window.showInvalidSku = function (requestedSkus, validSkus) {
    var validLower = (validSkus || []).map(function (sku) { return qoNormalizeSku(sku); });
    var invalid = [];

    (requestedSkus || []).forEach(function (sku) {
        var str = String(sku);
        if (str !== '-' && validLower.indexOf(qoNormalizeSku(str)) === -1) invalid.push(str);
    });

    if (!invalid.length) return;
    var invalidStr = invalid.join(', ');

    qoClearInvalidMessage();
    var quickOrderRow = document.querySelector('.quickorder .qo-product-items');
    if (quickOrderRow) {
        quickOrderRow.insertAdjacentHTML(
            'afterend',
            '<p class="invalid-sku-message"><strong>Invalid product SKU:</strong> ' + invalidStr + '</p>'
        );
    }

    var skuModal = document.querySelector('#skuContainer');
    if (!skuModal) {
        var body = document.querySelector('body');
        if (body) {
            body.insertAdjacentHTML('beforeend',
                '<div id="skuContainer" class="sku_modal">' +
                    '<div class="sku_modal-content">' +
                        '<h5>Invalid product SKU:</h5>' +
                        '<p>' + invalidStr + '</p>' +
                        '<a class="sku_close button button--primary">Close</a>' +
                    '</div>' +
                '</div>');
            skuModal = document.querySelector('#skuContainer');
        }
    } else {
        var pEl = skuModal.querySelector('p');
        if (pEl) pEl.innerHTML = invalidStr;
    }

    if (skuModal) skuModal.classList.add('modal-is-active');
};

/* -----------------------------------------------------------------------------
   Public entry points
   -------------------------------------------------------------------------- */

// Add one product row, optionally pinned to the variant that owns `sku`.
// Exposed so the quick-order template's search typeahead can use it too.
window.quickOrderAddItem = function (handle, sku, quantity) {
    return qoFetchRow({ handle: handle, sku: sku, qty: quantity || 1 }).then(function (row) {
        return row ? qoAppendRow(row) : null;
    });
};

// Entry point for the CSV dropzone and any pasted SKU list.
// skus: [{ sku: 'P1-1065-12', qty: 3 }, ...]
function quickOrderPopulate(skus) {
    qoClearInvalidMessage();
    window.validSku = [];

    // Merge duplicate SKUs and sum their quantities, keeping first-seen casing.
    var skuMap = {};
    var order = [];
    for (var i = 0; i < (skus || []).length; i++) {
        var key = qoNormalizeSku(skus[i].sku);
        var qty = parseInt(skus[i].qty, 10) || 0;
        if (!key || String(skus[i].sku).trim() === '-') continue;

        if (!skuMap[key]) {
            skuMap[key] = { sku: String(skus[i].sku).trim(), qty: 0 };
            order.push(key);
        }
        skuMap[key].qty += qty;
    }

    var grouped = order.map(function (key) { return skuMap[key]; });
    var requested = grouped.map(function (g) { return g.sku; });
    if (!requested.length) return Promise.resolve();

    return qoResolveViaMiddleware(requested)
        .then(function (map) {
            // Whatever the middleware couldn't place is almost always a
            // non-first variant SKU. Boost knows those.
            var unresolved = grouped
                .filter(function (g) { return !map[qoNormalizeSku(g.sku)]; })
                .map(function (g) { return g.sku; });

            return qoResolveViaBoost(unresolved).then(function (extra) {
                for (var key in extra) { if (extra.hasOwnProperty(key)) map[key] = extra[key]; }
                return map;
            });
        })
        .then(function (map) {
            var resolved = [];
            var invalid = [];

            grouped.forEach(function (g) {
                var handle = map[qoNormalizeSku(g.sku)];
                if (handle) resolved.push({ handle: handle, sku: g.sku, qty: g.qty });
                else invalid.push(g.sku);
            });

            // Request in parallel, append in the order the customer listed them.
            return qoMapLimit(resolved, 6, qoFetchRow).then(function (rows) {
                rows.forEach(function (row, index) {
                    if (row) {
                        qoAppendRow(row);
                        window.validSku.push(row.item.sku);
                    } else {
                        // Product page failed to load - treat as unavailable.
                        invalid.push(resolved[index].sku);
                    }
                });

                if (invalid.length) window.showInvalidSku(invalid, window.validSku);
            });
        })
        .catch(function (err) {
            console.error('[quick-order] populate failed:', err);
            window.showInvalidSku(requested, window.validSku || []);
        });
}

/* -----------------------------------------------------------------------------
   Page wiring
   -------------------------------------------------------------------------- */

$(function () {
    if ($("#asset-dropzone").length === 0) return;

    $("#asset-dropzone").dropzone({
        paramName: "file", // The name that will be used to transfer the file
        maxFilesize: 5, // MB,
        autoProcessQueue: true,
        createImageThumbnails: false,
        timeout: null,
        init: function () {
            this.on("processing", function () {
                $(".dropzone-loading").show();
            });
            this.on("error", function () {
                $(".dropzone-loading").hide();
                this.removeAllFiles();
                alert("SKU Upload", "Sorry, we experienced an error reading that file. Please try again or choose a different file.", "error");
            });
            this.on("queuecomplete", function () {
                this.removeAllFiles();
                $(".dropzone-loading").hide();
            });
            this.on("success", function (file, resp) {
                if (resp.success) {
                    quickOrderPopulate(resp.skus);
                } else {
                    alert("SKU Upload", "Sorry, we experienced an error reading that file. Please try again or choose a different file.", "error");
                }
            });
        }
    });

    $("body").on("click", ".qo-remove", function (e) {
        e.preventDefault();
        $(this).closest(".product-item-wrapper").remove();
        if ($(".quickorder-container .product-item-wrapper").length === 0) {
            $(".quickorder-container .qo-populated").hide();
        }
    });

    $("body").on("click", ".qo-reset", function () {
        $(".product-item-wrapper").remove();
        $(".quickorder-container .qo-populated").hide();
        qoClearInvalidMessage();
    });

    // Variant dropdown: refresh price, image, SKU and links for the new variant.
    $("body").on("change", ".qo-variant", function () {
        var $select = $(this);
        var variantId = $select.val();
        var $row = $select.closest('.product-item-wrapper');
        var productHandle = $row.attr('data-handle');

        if (!productHandle || !variantId) return;

        // SKU and image come from the product JSON. The SKU specifically must
        // NOT be read out of the ?view=quickorder markup - that always renders
        // the first variant's SKU, which is what bug 3 was.
        qoGetProduct(productHandle).then(function (product) {
            var variant = qoFindVariantById(product, variantId);
            if (variant && variant.sku) {
                $row.attr('data-sku', variant.sku);
                $row.data('sku', variant.sku);
                $row.find('.qo-sku input').val(variant.sku);
            }
            qoUpdateRowImage($row, product, variant);
        });

        // Price and the product links still come from the rendered row, which
        // does respect ?variant=.
        $.get('/products/' + productHandle + '?view=quickorder&variant=' + variantId, function (data) {
            if (!data || data.indexOf('data-unavailable-product') > -1 || data.indexOf('<meta charset="utf-8">') > -1) return;

            var $newData = $('<div>').html(data);

            var $newPrice = $newData.find('.qo-price');
            if ($newPrice.length) $row.find('.qo-price').html($newPrice.html());

            var newVariantUrl = '/products/' + productHandle + '?variant=' + variantId;
            $row.find('.qo-img a').first().attr('href', newVariantUrl);
            $row.find('.qo-product-title a').attr('href', newVariantUrl);
        }).fail(function () {
            console.error('[quick-order] failed to update variant price');
        });
    });
});

document.addEventListener('click', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('sku_close')) {
        e.preventDefault();
        var skuModal = document.querySelector('#skuContainer');
        if (skuModal) skuModal.classList.remove('modal-is-active');
    }
});
